import { useMutation } from '@apollo/client';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useMemo, useState } from 'react';
import { CREATE_ORG_SUMMARY_TAGS } from 'src/graphql/c_tags/queries';

const parseTags = (text, existingTitles) => {
  const seen = new Set(existingTitles.map((title) => title.trim().toLowerCase()));
  const tags = [];
  let skipped = 0;

  text.split(/[,\r\n]+/).forEach((line) => {
    const title = line.trim();
    if (!title) return;
    const key = title.toLowerCase();
    if (seen.has(key)) {
      skipped += 1;
      return;
    }
    seen.add(key);
    tags.push(title);
  });

  return { tags, skipped };
};

export default function BulkAddTagsDialog({ organizationId, existingTitles, onClose, onAdded }) {
  const { enqueueSnackbar } = useSnackbar();
  const [text, setText] = useState('');
  const [createTags, { loading }] = useMutation(CREATE_ORG_SUMMARY_TAGS);

  const { tags, skipped } = useMemo(() => parseTags(text, existingTitles), [text, existingTitles]);

  const onSubmit = async () => {
    try {
      await createTags({
        variables: {
          data: tags.map((title) => ({ organization_id: organizationId, title })),
        },
      });
      enqueueSnackbar(`Added ${tags.length} tag${tags.length === 1 ? '' : 's'}`);
      onAdded();
      onClose();
    } catch (error) {
      enqueueSnackbar(error?.message || 'Failed to add tags', { variant: 'error' });
    }
  };

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={loading ? undefined : onClose}>
      <DialogTitle>Add multiple tags</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Separate tags with commas or new lines. Blank entries and tags that already exist are
          skipped.
        </Typography>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={8}
          maxRows={16}
          placeholder={'Tag one, Tag two\nTag three'}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {tags.length} new tag{tags.length === 1 ? '' : 's'}
          {skipped > 0 && `, ${skipped} duplicate${skipped === 1 ? '' : 's'} skipped`}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button variant="contained" onClick={onSubmit} disabled={loading || tags.length === 0}>
          {loading ? 'Adding...' : `Add ${tags.length} tag${tags.length === 1 ? '' : 's'}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
